// Posts to the backend GA4 forwarder, which holds the api_secret. Not a Firebase SDK: that would target a different GA4 property than mobile.
// Without consent every request gets a fresh random clientId and no device storage, so the pre-consent pageview never touches the consent trigger.
import type { AnalyticsCollectRequest } from '../types/models'
import { redactUrl } from './redactUrl'

const CONSENT_KEY = 'recall_cookie_consent'
const CID_KEY = 'recall_analytics_cid'
const SID_KEY = 'recall_analytics_sid'
const SID_TS_KEY = 'recall_analytics_sid_ts'
// GA4 convention: a session ends after 30 min of inactivity, then a new one begins.
const SESSION_WINDOW_MS = 30 * 60 * 1000

const BASE = import.meta.env.GCP_API_URL as string
const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) || undefined

let platform = 'web'
// Pseudonymous Supabase id, never an email. Only sent in accepted mode.
let userId: string | null = null

export function setAnalyticsPlatform(value: string): void {
  platform = value
}

export function setAnalyticsUserId(id: string | null): void {
  userId = id
}

function consentAccepted(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted'
  } catch {
    return false
  }
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function persistentClientId(): string {
  let id = localStorage.getItem(CID_KEY)
  if (!id) {
    id = uuid()
    localStorage.setItem(CID_KEY, id)
  }
  return id
}

// Engagement is sent so GA4 session duration is real; a zero-length session would read as 1.
function currentSession(now: number): { sessionId: string; engagementMs: number } {
  const last = Number(localStorage.getItem(SID_TS_KEY) ?? 0)
  let id = localStorage.getItem(SID_KEY)
  const newSession = !id || now - last > SESSION_WINDOW_MS
  if (newSession) {
    id = now.toString()
    localStorage.setItem(SID_KEY, id)
  }
  localStorage.setItem(SID_TS_KEY, now.toString())
  return { sessionId: id as string, engagementMs: newSession ? 0 : now - last }
}

function buildBody(name: string, params: Record<string, string>): AnalyticsCollectRequest {
  const events = [{ name, params }]
  if (!consentAccepted()) {
    // A new id per request: nothing identifying, nothing persisted.
    return { clientId: uuid(), platform, events }
  }
  const { sessionId, engagementMs } = currentSession(Date.now())
  return {
    clientId: persistentClientId(),
    platform,
    sessionId,
    engagementTimeMsec: engagementMs,
    ...(userId ? { userId } : {}),
    ...(APP_VERSION ? { appVersion: APP_VERSION } : {}),
    events,
  }
}

/** Sends a single event. Returns a promise callers may ignore; never rejects. */
export async function logEvent(
  name: string,
  params: Record<string, string> = {},
): Promise<void> {
  let body: AnalyticsCollectRequest
  try {
    body = buildBody(name, params)
  } catch {
    return // localStorage blocked: drop the event.
  }
  try {
    await fetch(`${BASE}/analytics/collect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Lets a navigation-away page_view flush.
      keepalive: true,
    })
  } catch {
    /* fire-and-forget */
  }
}

// Must redact: this runs before supabase-js strips auth params, so a raw href would leak live tokens to GA4. See redactUrl.
export function trackPageView(): Promise<void> {
  const referrer = document.referrer ? redactUrl(document.referrer) : ''
  return logEvent('page_view', {
    page_location: redactUrl(window.location.href),
    page_path: window.location.pathname,
    page_title: document.title,
    ...(referrer ? { page_referrer: referrer } : {}),
  })
}
